'use client'

import { useEffect, useRef } from 'react'
import { event } from '@/lib/analytics'

export interface AnalyticsTags {
  'data-analytics-id': string
  'data-analytics-action'?: string
  'data-analytics-category'?: string
}

// Define the ClickEvent interface as specified in the requirements
export interface ClickEvent {
  tags: AnalyticsTags;
  elementType: string;
}

function extractTags(element: HTMLElement): AnalyticsTags | undefined {
  const id = element.getAttribute('data-analytics-id');
  if (!id) {
    return undefined
  }
  return {
    "data-analytics-id": id,
    "data-analytics-action": element.getAttribute('data-analytics-action') || undefined,
    "data-analytics-category": element.getAttribute('data-analytics-category') || undefined,
  }
}

/**
 * Utility function to extract a stable identifier from an element
 * Prioritizes data-analytics-id attribute, then id, then other fallbacks
 */
function getAnalyticsTags(element: HTMLElement): AnalyticsTags | undefined {
  // Check for data-analytics-id attribute (preferred)
  const analyticsId = element.getAttribute('data-analytics-id');
  if (analyticsId) {
    return extractTags(element)
  }

  // Check for id attribute
  if (element.id) {
    return {"data-analytics-id": element.id};
  }

  // Fallback to other identifiers. Try aria-label.
  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel) {
    return gaId(`aria:${ariaLabel}`);
  }

  if (element.tagName === 'BUTTON') {
    const text = element.textContent?.trim();
    if (text) {
      return gaId(`${element.tagName.toLowerCase()}:${text.substring(0, 20)}`);
    }
  }

  return gaId(`${element.tagName.toLowerCase()}:unknown`);
}

/**
 * Hook to track click events on interactive elements
 */
export function useClickTracker() {
  const initialized = useRef(false);

  useEffect(() => {
    // Avoid duplicate initialization
    if (initialized.current) {
      return;
    }
    initialized.current = true;

    // Use event delegation to minimize the number of event listeners
    const handleClick = (e: MouseEvent) => {
      // Find the clicked element
      const target = e.target as HTMLElement;
      if (!target) return;

      // Find the closest interactive element (button, link, etc.)
      const interactiveElement = findClosestInteractiveElement(target);
      if (!interactiveElement) return;
      const tags = getAnalyticsTags(interactiveElement);
      if (!tags)  {
        return;
      }

      // Create the click event data
      const clickEvent: ClickEvent = {
        tags,
        elementType: interactiveElement.tagName.toLowerCase(),
      };

      // Send the event to PostHog
      sendClickEventToPostHog(clickEvent);
    };

    // Add the event listener to the document body
    document.body.addEventListener('click', handleClick);

    // Clean up the event listener on unmount
    return () => {
      document.body.removeEventListener('click', handleClick);
    };
  }, []);
}

export function findClosestInteractiveElement(target: HTMLElement): HTMLElement | null {
  // List of interactive element tags
  const interactiveTags = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'DETAILS', 'SUMMARY'];

  // Check if the target itself is interactive
  if (interactiveTags.includes(target.tagName)) {
    return target;
  }

  // Check for elements with interactive roles
  const interactiveRoles = ['button', 'link', 'checkbox', 'menuitem', 'tab', 'radio'];
  const role = target.getAttribute('role');
  if (role && interactiveRoles.includes(role)) {
    return target;
  }

  // Check for elements with click handlers
  // This is a heuristic and not 100% reliable
  const hasClickHandler = target.hasAttribute('onclick') ||
                          target.hasAttribute('onClick') ||
                          target.hasAttribute('data-analytics-id');
  if (hasClickHandler) {
    return target;
  }

  // Look for closest interactive parent
  let current = target.parentElement;
  while (current) {
    if (interactiveTags.includes(current.tagName)) {
      return current;
    }

    const parentRole = current.getAttribute('role');
    if (parentRole && interactiveRoles.includes(parentRole)) {
      return current;
    }

    const parentHasClickHandler = current.hasAttribute('onclick') ||
                                 current.hasAttribute('onClick') ||
                                 current.hasAttribute('data-analytics-id');
    if (parentHasClickHandler) {
      return current;
    }

    current = current.parentElement;
  }

  // If no interactive element found, return null
  return null;
}

/**
 * Send the click event to PostHog
 */
function sendClickEventToPostHog(e: ClickEvent) {
  event({
    eventName: e.tags["data-analytics-id"] || 'click',
    category: e.tags["data-analytics-category"] || 'general_navigation',
    label: e.tags["data-analytics-action"],
  });
}

/**
 * Component that initializes click tracking
 */
export function ClickTracker() {
  useClickTracker();
  return null;
}

/**
 * Helper function to add data analytics attributes to an element
 * This can be used as a prop for React components.
 */
export function gaId(event: string, action?: string, category?: string): AnalyticsTags {
  return { 'data-analytics-id': event, 'data-analytics-action': action, 'data-analytics-category': category };
}
